import { Control, FieldValues, Path } from 'react-hook-form';

import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

interface ToggleWithDescriptionProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  description?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Toggle switch component with label and optional description.
 * Designed for boolean fields with explanatory text.
 *
 * Features:
 * - Clean toggle switch UI
 * - Label and description support
 * - React Hook Form integration
 * - Accessible with proper ARIA attributes
 * - Disabled state support
 *
 * @example
 * ```tsx
 * <ToggleWithDescription
 *   name="isManager"
 *   control={form.control}
 *   label="Manager"
 *   description="When checked, this user can be assigned as a project manager"
 * />
 *
 * <ToggleWithDescription
 *   name="placeholder"
 *   control={form.control}
 *   label="Placeholder"
 *   description="A placeholder user is a planning-only entry that represents a temporary or future team member"
 * />
 * ```
 */
export function ToggleWithDescription<T extends FieldValues>({
  name,
  control,
  label,
  description,
  disabled = false,
  className,
}: ToggleWithDescriptionProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn('space-y-0', className)}>
          <div className="flex items-center gap-4 rounded-md border border-border px-3.5 py-3">
            <div className="flex flex-1 flex-col gap-0.5">
              <label
                className={cn(
                  'text-sm font-semibold leading-5 text-foreground',
                  disabled && 'cursor-not-allowed opacity-70'
                )}
              >
                {label}
              </label>
              {description && (
                <p
                  className={cn(
                    'text-xs font-normal leading-4 text-muted-foreground',
                    disabled && 'opacity-70'
                  )}
                >
                  {description}
                </p>
              )}
            </div>
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} disabled={disabled} />
            </FormControl>
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
